// tslint:disable: no-magic-numbers
import { HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { dependencies } from './workflow.dependency';
import { Backend, JSONValue, PrimitiveType } from './workflow.type';
import { mergeJson } from '../helper/merge-json/merge-json';

import { PATH_ITERATION_SYMBOL, StatefulValue } from './stateful-value.class';
import { ErrCallback, FlatData, StorageBackend } from '../class/storage-backend.base';
import { RequestHeaderConst } from '@workflow/ecp-shared/constant/information.const';
import { StatefulService } from '@app/service/stateful.service';

const SUBOBJECT_BACKFILL = {
  'employments/-': { address: {} },
  'liabilities/-': { payoffInformation: {} },
  'realEstates/-': { address: {} }
};

const TIMEZONELESS_DATE_LEN = 19;

interface ApiRequest {
  array: {
    op: 'add' | 'remove';
    value?: FlatData;
    storageId: string;
    rootPath: string;
    cb?(err?: Error): void;
  }[];
  load: {
    storageId: string;
    cb(err?: Error, value?: PrimitiveType): void;
  }[];
  save: {
    value: PrimitiveType;
    storageId: string;
    cb(err?: Error): void;
  }[];
}

interface NestedData {
  [storageId: string]: PrimitiveType | NestedData;
}

export interface BundleValidation {
  errorCount: number;
  errors: { [path: string]: string[] };
  percentCompletion: number;
  totalCount: number;
}

interface BundleResponse {
  data: NestedData;
  validation: BundleValidation;
}

interface SessionStorageRequest {
  op: string;
  path: string;
  value?: PrimitiveType | JSONValue;
}

/**
 * Crud API back-end to allow field based loads, and maskarade the complexity of
 * simultaneous loads, merging loads per endpoint, etc
 */
export class CrudApiBackend extends StorageBackend {
  statefulService!: StatefulService; // Injected later

  constructor(
    protected readonly protocol: string,
    protected readonly data: FlatData,
    protected readonly arraySizes: { [rootPath: string]: number },
    protected readonly endpointPath: string,
    protected readonly token: string,
    statefulService: StatefulService // ✅ Inject `StatefulService`
  ) {
    super(protocol, data, arraySizes, endpointPath, token, statefulService);
  }

  public readonly type: Backend = 'api';

  /**
   * Validation update handler. BundleValidationAndProgressService should tap into this
   */
  public readonly validationUpdate = new Subject<BundleValidation>();

  /**
   * Endpoint for the dynamic bundle
   */
  // TODO: Didn't like it this way. Since the endpointPath is coming in from
  // appInit -> workflow.ola/oao["storage-info"].backend.api.endpoint. Will modify that & come up here in a future PR
  private readonly bundleUrl = `${dependencies.configService.apiRoot}${this.endpointPath?.toString().replace('/api', '')}`;

  /**
   * Session ID / AccessToken
   */
  public authorization: StatefulValue;

  /**
   * Bundle creation has been attempted. Pending request callbacks (array of callbacks), finished with success (= true) or error
   */
  private bundleCreation: ErrCallback[] | boolean | Error = [];

  /**
   * Ongoing HTTP GET or PATCH request
   */
  private currentReq: ApiRequest;

  /**
   * Flag to determine if the PATCH call is from the Request.Save
   */
  private isRequestFromSave = false;

  /**
   * Flag to determine when data is saved to Session Storage
   */
  private isDataSavedToSS = false;

  /**
   * Session Variable to hold the request body/payload of PATCH
   */
  private sessionStorageRequestBody = [];

  /**
   * Variable to hold the request body/payload for PATCH call
   */
  private patchRequestBody = [];

  /**
   * Pending HTTP GET or PATCH request (waiting for current request to finish)
   */
  private nextReq: ApiRequest = { array: [], load: [], save: [] };

  /**
   * Request expansion of array
   * @param rootPath the path before the '$'
   * @param appendData object {fullpath: value} to save on newly created array item
   * @param cb callback function
   */
  public arrayAdd(rootPath: string, cb?: ErrCallback): void;
  public arrayAdd(rootPath: string, appendData: FlatData, cb?: ErrCallback): void;
  public arrayAdd(rootPath: string, arg1: FlatData | ErrCallback, arg2?: ErrCallback): void {
    const cb = arg2 || (typeof arg1 === 'function' && arg1);
    const value = (typeof arg1 === 'object' && arg1) || {};
    for (const [longPath, val] of Object.entries(value)) {
      delete value[longPath];
      let shortPath = longPath.substr(rootPath.length);
      shortPath = shortPath.substr(shortPath.indexOf(PATH_ITERATION_SYMBOL) + 1);
      while (shortPath[0] === '/') {
        shortPath = shortPath.substr(1);
      }
      if (shortPath.indexOf('/') > -1) {
        throw new Error('appendData must not have multilevel hierarchy');
      }
      value[shortPath] = val;
    }

    this.nextReq.array.push({ op: 'add', rootPath, storageId: `${rootPath}/-`, cb, value });
    this.nextRequest();
  }

  /**
   * Request removal of array index on backend
   * @param rootPath the path before the '$'
   * @param index 0-based element index (last index on null)
   * @param cb callback function
   */
  public arrayRemove(rootPath: string, index?: number, cb?: ErrCallback): void {
    const idx = index == null ? this.arraySizes[`${this.protocol}:${rootPath}`] - 1 : index;
    this.nextReq.array.push({ op: 'remove', rootPath, storageId: `${rootPath}/${idx}`, cb });
    this.nextRequest();
  }

  /**
   * @inheritdoc
   */
  public arrayTruncate(rootPath: string, cb?: ErrCallback): void {
    for (let i = this.arraySizes[`${this.protocol}:${rootPath}`] - 1; i >= 0; i--) {
      this.nextReq.array.push({ op: 'remove', rootPath, storageId: `${rootPath}/${i}`, cb: !i ? cb : null });
    }
    this.nextRequest();
  }

  public clear(): void {
    Object.keys(this.data).forEach((key) => {
      delete this.data[key];
    });
    this.initialized = false;
    this.authorization.value = '';
  }

  /**
   * Temporary measure to ensure bundle creation
   * @param cb callback once bundle creation is done
   */
  public checkBundle(cb: ErrCallback): void {
    if (Array.isArray(this.bundleCreation)) {
      this.bundleCreation.push(cb);
    } else if (this.bundleCreation === true) {
      cb();
    } else {
      cb(this.bundleCreation as Error);
    }
  }

  /**
   * Common headers
   */
  public headers(token?: string): HttpHeaders {
    const headers = {
      // Temporarily duplicating sessionID as we move toward JWTs in Authorization header.
      [RequestHeaderConst.authorization]: this.authorization.value as string,
      [RequestHeaderConst.dxSessionId]: this.authorization.value as string,
      [RequestHeaderConst.contentTypeName]: 'application/json',
      [RequestHeaderConst.apiVersion]: '1.0'
    };

    if (token) {
      headers['x-api-recaptcha'] = token;
    }

    for (const key of Object.keys(headers)) {
      if (!headers[key]) {
        delete headers[key];
      }
    }
    return new HttpHeaders(headers);
  }

  public getBundle = (resolve?, reject?): void => {
    dependencies.httpClient.get<BundleResponse>(this.bundleUrl, { headers: this.headers() }).subscribe({
      next: (res) => {
        dependencies.stateManagementService.set(this.protocol, res.data);
        const flatData = this.flattenData(this.protocol, res.data);
        this.syncDump(flatData);
        this.propagateBundle.next();
        if (res.validation) {
          this.validationUpdate.next(res.validation);
        }
        this.response();
        if (resolve) {
          resolve(`Bundle is loaded for ${this.protocol} ${this.bundleUrl}`);
        }
      },
      error: (err) => {
        this.response(err);
        if (reject) {
          reject(`Bundle failed to load for ${this.protocol} ${this.bundleUrl} with error ${err}`);
        }
      }
    });
  };

  response = (err?: Error): void => {
    const list = this.bundleCreation as ErrCallback[];
    this.bundleCreation = err || true;
    const cbs = list && list.length ? list : [];
    for (const cb of cbs) {
      cb(err);
    }
  };

  /**
   * Temporary measure to set up bundle until the API side changes
   * Should be called from wherever this service is initialized
   * @param authorization session storage authorization / access token container
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public init(): Promise<any> {
    if (this.initialized) {
      return;
    }

    return new Promise((resolve, reject) => {
      if (!this.token) {
        throw new Error(`Token storage config is missing for backend ${this.protocol}.
                          Make sure you add token config in storage-info.json for this protocol. Example: { toke: 'ss: authorization' }`);
      }

      this.initialized = true;

      this.authorization = this.statefulService.wrap(this.token);
      this.authorization.load();

      this.bundleCreation = [];

      if (this.authorization.value) {
        this.getBundle(resolve, reject);
        return;
      }

      dependencies.httpClient
        .post<{ data: { AccessToken: string; referenceFolderId: string } }>(this.bundleUrl, '{}', { headers: this.headers() })
        .toPromise()
        .then((res) => {
          if (res && res.data && res.data.AccessToken) {
            this.authorization.value = res.data.AccessToken;
            this.getBundle(resolve, reject);
          } else {
            this.response(new Error('Insufficient data'));
            reject(`Bundle failed to create for ${this.protocol} ${this.bundleUrl}`);
          }
        })
        .catch((err) => {
          console.error('Error creating bundle', err);
          this.response(err);
        });
    });
  }

  /**
   * Get access to token storage
   */
  public get tokenStorage(): StatefulValue {
    return this.authorization;
  }

  /**
   * @inheritdoc
   */
  public load(storageId: string, cb: (err?: Error, data?: PrimitiveType) => void): void {
    this.nextReq.load.push({ cb, storageId });
    this.nextRequest();
  }

  /**
   * @inheritdoc
   */
  public save(storageId: string, val: PrimitiveType, cb: ErrCallback): void {
    let value = val;
    if (typeof value === 'string') {
      const info = dependencies.storageInfoService.parse('api:' + storageId);
      if (info && info.config && info.config.type === 'number') {
        value = value.replace(/\$|,/g, '');
      }
    }
    this.nextReq.save.push({ storageId, value, cb });
    this.nextRequest();
  }

  /**
   * Expose retrieving state by checking for pending GET requests
   */
  protected get retrieving(): boolean {
    return (
      !Object.keys(this.data).find((key) => key.split(':')[0] === this.protocol) ||
      (this.currentReq && this.currentReq.load.length > 0) ||
      this.nextReq.load.length > 0
    );
  }

  /**
   * Expose saving state by checking for pending PATCH requests
   */
  protected get saving(): boolean {
    return (
      (this.currentReq && (this.currentReq.save.length > 0 || this.currentReq.array.length > 0)) ||
      this.nextReq.save.length > 0 ||
      this.nextReq.array.length > 0
    );
  }

  /**
   * Transform data hierarchy to flat map of data
   * @param nested object, array, or value to exemine
   * @param flat object containing the flattened map, reference passed for each recursion (optional for top level call)
   * @param prefix path built up in recursion (optional for top level call)
   */
  private flattenData(protocol: string, nested: PrimitiveType | NestedData, flat: FlatData = {}, prefix = ''): FlatData {
    if (Array.isArray(nested)) {
      nested.forEach((item: NestedData, i) => {
        this.flattenData(protocol, item, flat, `${prefix ? prefix + '/' : ''}${i}`);
      });
    } else if (nested && typeof nested === 'object') {
      for (const key of Object.keys(nested)) {
        this.flattenData(protocol, nested[key] as NestedData, flat, `${prefix ? prefix + '/' : ''}${key}`);
      }
    } else {
      const storageId = this.pathRemapForUI(prefix);
      const info = dependencies.storageInfoService.parse(`${protocol}:` + storageId);
      if (info && info.config && (info.config.type === 'date' || info.config.type === 'dateMoYear') && nested) {
        if ((typeof nested === 'string' && nested.indexOf('T') > -1) || typeof nested === 'number') {
          try {
            const date = new Date(typeof nested === 'string' ? nested.substr(0, TIMEZONELESS_DATE_LEN) : nested);
            const [y, m, d] = [date.getFullYear(), date.getMonth() + 1, date.getDate()];
            flat[storageId] = `${(m < 10 ? '0' : '') + m}${info.config.type === 'date' ? `/${d < 10 ? '0' : ''}${d}` : ''}/${y}`;
          } catch (e) {}
        }
      } else {
        flat[storageId] = nested as PrimitiveType;
      }
    }
    return flat;
  }

  /**
   * Make HTTP request for next pending requests, if any
   */
  private nextRequest(): void {
    if (!this.currentReq) {
      if (this.nextReq.array.length || this.nextReq.load.length || this.nextReq.save.length) {
        const array: ApiRequest['array'] = [...this.nextReq.array];

        if (array.length) {
          this.currentReq = { array, load: [], save: [] };
          this.nextReq.array = [];
        } else if (this.nextReq.load.length) {
          this.currentReq = { array: [], load: this.nextReq.load, save: [] };
          this.nextReq.load = [];
        } else {
          this.currentReq = this.nextReq;
          this.nextReq = { array: [], load: [], save: [] };
        }
        const req = this.currentReq;

        this.checkBundle((err?: Error) => {
          if (err) {
            this.respond(err);
            return;
          }
          const observable = this.processRequest(req);
          if (observable == null && this.isDataSavedToSS) {
            this.respond();
          } else {
            if (observable) {
              observable
                .toPromise()
                .then((result) => {
                  this.respond(null, result);
                })
                .catch((reqErr: HttpErrorResponse) => {
                  this.respond(reqErr, !(reqErr.error instanceof Error) && reqErr.error);
                });
            }
          }
        });
      }
    }
  }

  /**
   * Processing the current request to return the appropriate observable
   * @param request Current request
   */
  private processRequest(request: ApiRequest): Observable<BundleResponse> {
    let observable: Observable<BundleResponse>;
    const headers = this.headers();
    if (request.array.length) {
      // modify array(s)
      const rawReqBody = request.array.map((item) => {
        // convert to array add / rm
        const value = item.value ? mergeJson(item.value, SUBOBJECT_BACKFILL[item.storageId] || {}) : undefined;
        return { op: item.op, path: this.pathRemapForAPI(item.storageId), value };
      });

      const saveAppDataToSessionStorageFF = sessionStorage.getItem('SaveAppDataToSessionStorage');
      if (JSON.parse(saveAppDataToSessionStorageFF)) {
        this.removeArrayItemsFromSessionStorage(rawReqBody);
      }
      this.loadGetAfterPatch(JSON.stringify(rawReqBody));
    } else if (request.save.length) {
      // patch
      if (!headers.keys().includes('Authorization')) {
        this.currentReq = null;
        this.isRequestFromSave = false;
        this.patchRequestBody = null;
        this.sessionStorageRequestBody = null;
        this.isDataSavedToSS = false;
        return;
      }

      observable = this.generateObservable(request, headers);
    } else {
      // get only
      observable = dependencies.httpClient.get<BundleResponse>(this.bundleUrl, { headers });
    }
    return observable;
  }

  /**
   * Method to delete/remove already stored array items in Session Storage if removed from UI
   * @param rawReqBody - current request body
   */
  private removeArrayItemsFromSessionStorage(rawReqBody: SessionStorageRequest[]): void {
    const itemsToRemove = rawReqBody.filter((item) => item.op === 'remove');
    if (itemsToRemove.length > 0) {
      this.handleSessionStorage(itemsToRemove);
      const removeStoragePath = itemsToRemove[0].path;
      const patchSessionStorageValue = this.statefulService.wrap('ss:current-patch-request-body').valueString;
      if (patchSessionStorageValue && patchSessionStorageValue.length > 0) {
        let parsedValue = JSON.parse(patchSessionStorageValue);
        parsedValue = parsedValue.filter((item) => !item.path.toString().includes(removeStoragePath));
        this.statefulService.wrap('ss:current-patch-request-body').value = JSON.stringify(parsedValue);
      }
    }
  }

  /**
   * Method to generate the observable based on the Save Application Data to Session Storage Feature Flag
   * @param request - current request
   * @param headers - http headers
   * @returns observable
   */
  private generateObservable(request: ApiRequest, headers: HttpHeaders): Observable<BundleResponse> {
    let observable: Observable<BundleResponse>;
    const saveAppDataToSessionStorageFF = sessionStorage.getItem('SaveAppDataToSessionStorage');
    if (Boolean(JSON.parse(saveAppDataToSessionStorageFF))) {
      // When Save Application Data to Session Storage Feature Flag is turned ON
      this.checkForAppOrigin();
      const rawReqBody = request.save
        .filter((item, i) => {
          // squash values for the same storage ID
          return !request.save.slice(i + 1).find((followUpItem) => followUpItem.storageId === item.storageId);
        })
        .map((item) => {
          // convert to bundle API request body
          // timestamp helps to remove only the updated request properly
          return { op: 'add', path: this.pathRemapForAPI(item.storageId), value: item.value, timestamp: Date.now() };
        });

      this.handleSessionStorage(rawReqBody);
      this.sessionStorageRequestBody = request.save;
      this.isDataSavedToSS = true;

      return null;
    } else {
      // When Save Application Data to Session Storage Feature Flag is turned OFF
      const rawReqBody = JSON.stringify(
        request.save
          .filter((item, i) => {
            // squash values for the same storage ID
            return !request.save.slice(i + 1).find((followUpItem) => followUpItem.storageId === item.storageId);
          })
          .map((item) => {
            // convert to bundle API request body
            return { op: 'add', path: this.pathRemapForAPI(item.storageId), value: item.value };
          })
      );

      this.patchRequestBody = request.save;
      this.isRequestFromSave = true;

      observable = dependencies.httpClient.patch<BundleResponse>(this.bundleUrl, rawReqBody, { headers });
    }
    return observable;
  }

  /**
   * Method to determine if the new app is coming from Post-App, if Yes - preparing the Session variables
   */
  private checkForAppOrigin(): void {
    // Why aren't these using the statefulService wrap function?
    const isFreshAppFromDashboard = this.statefulService.wrap('ss:fresh-app-from-postapp').value as boolean;
    if (isFreshAppFromDashboard) {
      this.statefulService.wrap('ss:current-patch-request-body').value = null;

      this.statefulService.wrap('ss:fresh-app-from-postapp').value = false;
    }
  }

  /**
   * Method to handle the data being saved into the Session storage
   * @param requestBody - current Request body
   */
  private handleSessionStorage(requestBody: SessionStorageRequest[]): void {
    const patchSessionStorageValue = this.statefulService.wrap('ss:current-patch-request-body').valueString;
    if (patchSessionStorageValue && patchSessionStorageValue.length > 0) {
      const parsedValue = JSON.parse(patchSessionStorageValue);
      requestBody.forEach((item) => {
        parsedValue.push(item);
      });
      this.statefulService.wrap('ss:current-patch-request-body').value = JSON.stringify(parsedValue);
    } else {
      this.statefulService.wrap('ss:current-patch-request-body').value = JSON.stringify(requestBody);
    }
  }

  /**
   * Making a GET call after the PATCH
   * @param rawReqBody Request body sending over to the PATCH API call
   */
  private loadGetAfterPatch(rawReqBody: string): void {
    const headers = this.headers();
    const observable = dependencies.httpClient.patch<BundleResponse>(this.bundleUrl, rawReqBody, { headers });

    observable.subscribe(() => {
      const obs = dependencies.httpClient.get<BundleResponse>(this.bundleUrl, { headers });
      obs
        .toPromise()
        .then((res) => {
          this.respond(null, res);
        })
        .catch((reqErr: HttpErrorResponse) => {
          this.respond(reqErr, !(reqErr.error instanceof Error) && reqErr.error);
        });
    });
  }

  /**
   * Response processing
   * @param err optional error
   * @param res data response from bundle API
   */
  private respond(err?: Error, res?: BundleResponse): void {
    const req = this.currentReq;

    if (this.isDataSavedToSS) {
      this.patchSyncDump(this.sessionStorageRequestBody);
      this.responseCallbacks(req.save);
      this.responseCallbacks(req.load);
      this.responseCallbacks(req.array);
      this.propagateBundle.next();
    } else {
      if (!res || !res.data) {
        this.responseCallbacks(req.save, err || new Error('Unknown Error'));
        this.responseCallbacks(req.load, err || new Error('Unknown Error'));
        this.responseCallbacks(req.array, err || new Error('Unknown Error'));
        this.handleError(res);
      } else if (this.isRequestFromSave) {
        this.patchSyncDump(this.patchRequestBody);
        this.responseCallbacks(req.save, err);
        this.responseCallbacks(req.array, err);
        this.propagateBundle.next();
      } else {
        const flatData = this.flattenData(this.protocol, res.data);
        this.syncDump(flatData);
        this.responseCallbacks(req.save, err);
        this.responseCallbacks(req.load, null, flatData);
        this.responseCallbacks(req.array, err);
        this.propagateBundle.next();
      }
    }

    if (res && res.validation) {
      this.validationUpdate.next(res.validation);
    }

    this.currentReq = null;
    this.isRequestFromSave = false;
    this.patchRequestBody = null;
    this.sessionStorageRequestBody = null;
    this.isDataSavedToSS = false;
    this.nextRequest();
  }

  private handleError(data): void {
    const message = data?.message ? data?.message : data?.apiErrorMessage;
    console.error('Error:', message);
  }

  /**
   * Respond to all callbacks on pending request list, and reset the list, start any pending PATCH request
   * @param list list of pending request w/ callbacks
   * @param err optional error
   * @param flatData optional data with storageId's as key
   */
  private responseCallbacks(list: ApiRequest[keyof ApiRequest], err?: Error, flatData?: FlatData): void {
    for (const item of list) {
      if (typeof item.cb === 'function') {
        item.cb(err, flatData && flatData[item.storageId]);
      }
    }
  }
}
